import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ViewField, ViewSchema } from "./dashboardViews/types";
import {
  readSessionView,
  readView,
  removeSessionView,
  removeView,
  writeSessionView,
  writeView,
} from "../utils/viewStorage";

export interface UseDashboardViewOptions {
  /** Live option lists, keyed by each field's optionsKey, used by validate(). */
  optionLists?: Record<string, unknown[]>;
  /**
   * False while any option list a field validates against is still loading.
   * A field cannot be safely validated (or safely defaulted) before then, so
   * the whole view is held not-ready rather than risk dropping a value that
   * is actually still valid.
   */
  optionListsReady?: boolean;
}

export interface UseDashboardViewResult {
  view: Record<string, unknown>;
  setValue: (key: string, value: unknown) => void;
  /** Sets several fields in one call: one address-bar replacement, one storage write. */
  setValues: (partial: Record<string, unknown>) => void;
  clearValue: (key: string) => void;
  clearAll: () => void;
  /** Keys whose resolved value differs from its schema default. */
  appliedKeys: string[];
  /** False until userDetails and every required option list have resolved. */
  isReady: boolean;
}

type FieldEntry = [string, ViewField<unknown>];

const defaultsOf = (fieldEntries: FieldEntry[]): Record<string, unknown> => {
  const defaults: Record<string, unknown> = {};
  fieldEntries.forEach(([key, field]) => {
    defaults[key] = field.default;
  });
  return defaults;
};

const resolveFromAddressBar = (
  searchParams: URLSearchParams,
  fieldEntries: FieldEntry[]
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  fieldEntries.forEach(([key, field]) => {
    const raw = searchParams.get(field.param);
    // Present-but-invalid still falls back to default here, not to storage:
    // a shared link partially blended with the recipient's own stored view
    // is the confusing case this guards against.
    result[key] =
      raw === null ? field.default : field.deserialise(raw) ?? field.default;
  });
  return result;
};

const resolveFromStorage = (
  dashboard: string,
  tenantId: number | undefined,
  userId: number,
  fieldEntries: FieldEntry[]
): Record<string, unknown> => {
  const storedValues = readView(dashboard, tenantId, userId);
  const storedSearch = readSessionView(dashboard, tenantId, userId);
  const result: Record<string, unknown> = {};
  fieldEntries.forEach(([key, field]) => {
    const raw = (field.session ? storedSearch : storedValues)[field.param];
    result[key] =
      raw === undefined ? field.default : field.deserialise(raw) ?? field.default;
  });
  return result;
};

const applyValidation = (
  raw: Record<string, unknown>,
  fieldEntries: FieldEntry[],
  optionLists: Record<string, unknown[]>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  fieldEntries.forEach(([key, field]) => {
    const value = raw[key];
    if (field.validate && field.optionsKey) {
      const options = optionLists[field.optionsKey] ?? [];
      if (!field.validate(value, options)) {
        result[key] = field.default;
        return;
      }
    }
    result[key] = value;
  });
  return result;
};

export const useDashboardView = (
  schema: ViewSchema,
  { optionLists = {}, optionListsReady = true }: UseDashboardViewOptions = {}
): UseDashboardViewResult => {
  const { userDetails } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldEntries = useMemo(
    () => Object.entries(schema.fields) as FieldEntry[],
    [schema]
  );

  const isReady = userDetails !== undefined && optionListsReady;

  // Decided once, at mount, and fixed for the mount's lifetime: any
  // recognised parameter present makes the address bar the sole source for
  // this mount, and storage is never consulted. Recomputing this reactively
  // would let the hook's own corrective address-bar rewrites (dropping an
  // invalid value, promoting a restored view into the URL) flip the source
  // mid-mount - e.g. dropping the one recognised parameter because its
  // value was invalid would make the next render look "bare" and fall
  // through to storage, blending a shared link with the recipient's own
  // stored view, which is exactly the confusing case this rule prevents.
  const [isAddressSourced] = useState(() =>
    fieldEntries.some(([, field]) => searchParams.has(field.param))
  );

  // Stage 1 (synchronous, mount): the address-sourced case needs neither
  // the user id nor option lists, so it renders immediately instead of
  // waiting behind the page's loader. The storage-sourced case starts at
  // schema defaults until Stage 2 can safely read storage.
  const [view, setView] = useState<Record<string, unknown>>(() =>
    isAddressSourced
      ? resolveFromAddressBar(searchParams, fieldEntries)
      : defaultsOf(fieldEntries)
  );

  // Stage 2 (once, when ready): the authoritative resolve-and-validate
  // pass. Runs exactly once per mount. After this, `view` is owned by this
  // hook's own state and changes only through the setters below - it is
  // deliberately NOT re-derived from the address bar or storage on every
  // render. That matters for two reasons: it keeps the locked source from
  // 3.4.1 meaningful for the whole mount, and it means a storage write that
  // silently fails (private browsing, a full quota) can never "snap back" a
  // change already applied in memory - the feature degrades to address-bar
  // persistence exactly as the tech spec requires, rather than reverting.
  const resolvedRef = useRef(false);
  useEffect(() => {
    if (!isReady || resolvedRef.current) return;
    resolvedRef.current = true;

    const raw = isAddressSourced
      ? resolveFromAddressBar(searchParams, fieldEntries)
      : resolveFromStorage(
          schema.dashboard,
          userDetails?.tenant,
          userDetails?.id as number,
          fieldEntries
        );

    setView(applyValidation(raw, fieldEntries, optionLists));
    // Deliberately one-shot: gated on isReady only. Everything else is read
    // fresh at the moment it fires, not tracked reactively.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const appliedKeys = useMemo(
    () =>
      fieldEntries
        .filter(([key, field]) => view[key] !== field.default)
        .map(([key]) => key),
    [view, fieldEntries]
  );

  // Passive correction only: keeps the address bar in sync with the
  // resolved view once Stage 2 has run - covers "storage restored on a
  // bare address" and "an invalid/stale value was dropped". User-initiated
  // changes go through persist(), which this then finds already in sync.
  useEffect(() => {
    if (!isReady || !resolvedRef.current) return;

    const params = new URLSearchParams(searchParams);
    let dirty = false;

    fieldEntries.forEach(([key, field]) => {
      const value = view[key];
      const desired = value === field.default ? null : field.serialise(value);
      const current = params.get(field.param);
      if (desired === current) return;
      dirty = true;
      if (desired === null) params.delete(field.param);
      else params.set(field.param, desired);
    });

    if (dirty) setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, view]);

  const persist = useCallback(
    (nextView: Record<string, unknown>) => {
      if (!isReady) return;

      setView(nextView);

      const params = new URLSearchParams(searchParams);
      const localValues: Record<string, string> = {};
      const sessionValues: Record<string, string> = {};

      fieldEntries.forEach(([key, field]) => {
        const value = nextView[key];
        params.delete(field.param);
        if (value === field.default) return;
        const serialised = field.serialise(value);
        if (serialised === null) return;
        params.set(field.param, serialised);
        if (field.session) sessionValues[field.param] = serialised;
        else localValues[field.param] = serialised;
      });

      setSearchParams(params, { replace: true });

      const tenantId = userDetails?.tenant;
      const userId = userDetails?.id as number;

      if (Object.keys(localValues).length > 0) {
        writeView(schema.dashboard, tenantId, userId, localValues);
      } else {
        removeView(schema.dashboard, tenantId, userId);
      }

      if (Object.keys(sessionValues).length > 0) {
        writeSessionView(schema.dashboard, tenantId, userId, sessionValues);
      } else {
        removeSessionView(schema.dashboard, tenantId, userId);
      }
    },
    [isReady, searchParams, fieldEntries, schema, userDetails, setSearchParams]
  );

  const setValue = useCallback(
    (key: string, value: unknown) => persist({ ...view, [key]: value }),
    [view, persist]
  );

  const setValues = useCallback(
    (partial: Record<string, unknown>) => persist({ ...view, ...partial }),
    [view, persist]
  );

  const clearValue = useCallback(
    (key: string) => {
      const field = schema.fields[key];
      if (!field) return;
      persist({ ...view, [key]: field.default });
    },
    [view, persist, schema]
  );

  const clearAll = useCallback(
    () => persist(defaultsOf(fieldEntries)),
    [persist, fieldEntries]
  );

  return { view, setValue, setValues, clearValue, clearAll, appliedKeys, isReady };
};
